import React from 'react';
import css from './modal.module.scss';
import { Modal as BaseModal } from '@mui/base';
import { ModalBackdropSlotProps } from '@mui/base/Modal/Modal.types';
import clsx from 'clsx';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { motion } from 'motion/react';

type Props = {
  size?: 'tiny' | 'small' | 'large' | 'full' | 'default';
  disableClose?: boolean;
  disableBackdropClose?: boolean;
};

const Content = ({
  children,
  rootRef,
}: React.PropsWithChildren<{
  rootRef?: React.MutableRefObject<HTMLDivElement | null>;
}>) => (
  <div className={css.content} ref={rootRef}>
    {children}
  </div>
);

const Backdrop = React.forwardRef<HTMLDivElement, ModalBackdropSlotProps>(
  (props, ref) => {
    const { open, ownerState, ...other } = props;

    return (
      <motion.div
        {...other}
        ref={ref}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />
    );
  },
);

export type ModalProps = Props & React.ComponentProps<typeof BaseModal>;

const Modal = ({
  children,
  size = 'default',
  disableClose = false,
  disableBackdropClose = false,
  disableEscapeKeyDown = false,
  onClose,
  ...props
}: ModalProps) => {
  const handleClose = (
    event: {},
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (disableBackdropClose && reason === 'backdropClick') {
      return;
    }

    onClose?.(event, reason);
  };

  return (
    <BaseModal
      {...props}
      disableEscapeKeyDown={disableEscapeKeyDown}
      onClose={handleClose}
      className={css.root}
      slotProps={{
        root: {
          className: clsx(css.root, css['size-' + size]),
        },
        backdrop: {
          className: css.backdrop,
        },
      }}
      slots={{
        backdrop: Backdrop,
      }}
    >
      <motion.div
        className={css.modal}
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 1.1, opacity: 0 }}
        transition={{ duration: 0.15 }}
        transformTemplate={({ scale }) =>
          `translate(-50%, -50%) scale(${scale})`
        }
      >
        {!disableClose && (
          <Button
            onClick={onClose}
            className={css['close-button']}
            startIcon={<Icon name={'fas fa-times'} />}
            variant={'text'}
          />
        )}
        {children}
      </motion.div>
    </BaseModal>
  );
};

export default Object.assign(Modal, {
  Content,
});
